import type { Components, Theme } from "@mui/material/styles";

import {
  borderWidth,
  color,
  effect,
  gradient,
  opacity,
  radius,
  spacing,
  transition,
} from "@app/theme/weaveTokens.ts";
import { typographyVariant } from "@app/theme/typography.ts";

type ThemeComponents = Components<Omit<Theme, "components">>;

/**
 * Restores the Digital HIG Search and Combobox looks. Both are MUI
 * Autocomplete under the hood and are styled through `AppSearch*` and
 * `AppCombobox*` classes, which Weave's Autocomplete theme (keyed on a
 * `variant="search"` prop instead) does not know about.
 */
export const autocompleteOverrides: ThemeComponents = {
  MuiAutocomplete: {
    styleOverrides: {
      root: {
        // Weave's IconButton gives every icon button a 1px border, a hover
        // and press ring, 12px side padding and a 2px outline focus ring, so
        // the popup chevron rendered in a bordered box and the clear button
        // was 42px wide. DHIG's chevron and clear button are bare 2px and 4px
        // padded icons. `&&` outranks Weave's `&.MuiIconButton-sizeSmall`.
        '&& [class*="MuiAutocomplete-popupIndicator"]': {
          border: "none",
          padding: "2px",
          "&:hover, &:active": { boxShadow: "none" },
          // Weave dims every disabled icon button, and DHIG dims the whole
          // field once, so the chevron was dimmed twice.
          "&.Mui-disabled": { opacity: 1 },
          // DHIG draws no ring here (and the chevron is not in the tab
          // order). Use the same box-shadow ring as the clear button instead
          // of Weave's outline, so a programmatic focus is still visible.
          "&:focus-visible": {
            outline: "none",
            outlineOffset: 0,
            boxShadow: effect.focusRingThin,
          },
        },
        '&& [class*="MuiAutocomplete-clearIndicator"]': {
          border: "none",
          padding: spacing.xxs,
          "&:hover, &:active": { boxShadow: "none" },
          "&.Mui-disabled": { opacity: 1 },
          "&:focus-visible": { outlineOffset: 0 },
        },
        '&[class*="AppCombobox"]': {
          '&[class*="MuiAutocomplete-hasPopupIcon"]': {
            '& [class*="MuiAutocomplete-inputRoot"]': {
              paddingRight: spacing.s,
            },
          },
          '& [class*="AppCombobox--textField"]': {
            '& [class*="AppCombobox--input"][class*="MuiInputBase-inputAdornedEnd"]':
              { padding: `${spacing.xs} ${spacing.s}` },
          },
        },
        '& [class*="AppCombobox--textField"]': {
          '& [class*="MuiInput-root"]': {
            display: "flex",
            flexWrap: "wrap",
            paddingRight: spacing.none,
            // DHIG's MuiInput input slot. Without it the input keeps MUI's
            // 1.4375em line height (field 39px instead of 40px) and loses the
            // negative margin that tucks it under the end adornment.
            '& [class*="MuiInputBase-input"]': {
              ...typographyVariant["input-value"],
              height: spacing.l,
              '&[class*="MuiInputBase-inputAdornedEnd"]': {
                marginRight: `-${spacing.xs}`,
              },
            },
            // DHIG's error shadow (Weave's is a 1px inset ring only).
            "&.Mui-error, &.Mui-error:hover, &.Mui-focused.Mui-error": {
              boxShadow: effect.fieldError,
            },
            // Weave's chip colour and hover ring; DHIG has neither here.
            '& [class*="MuiChip-root"]': {
              color: color.ink,
              "&:hover": { boxShadow: "none" },
            },
          },
          '& [class*="MuiAutocomplete-endAdornment"]': {
            display: "flex",
            flexDirection: "row",
            marginRight: "0",
            top: spacing.xs,
            right: spacing.xs,
            minWidth: "fit-content",
            gap: spacing.xxs,
            "& button": { color: color.ink },
          },
        },
        '&[class*="AppCombobox--multiple"]': {
          '& [class*="AppCombobox--textField"]': {
            '& [class*="MuiInput-root"]': {
              paddingLeft: spacing.s,
              paddingBottom: spacing.xs,
              minHeight: spacing.xxl,
              '& [class*="MuiChip-root"]': {
                margin: `${spacing.xs} 2px ${spacing.none}`,
              },
            },
          },
        },
        '&[class*="AppCombobox--layout--grow"]': {
          '& [class*="AppCombobox--textField"]': {
            maxHeight: "212px",
            '& [class*="MuiInput-root"]': {
              '&[class*="MuiInputBase-root"]': {
                "&.Mui-focused:not(.Mui-disabled):before": {
                  boxShadow: "none",
                },
              },
              overflow: "auto",
              paddingRight: "46px",
              "&::before": { borderBottom: "none" },
              "&::after": { borderBottom: "none" },
              "&:hover": { "&::before": { boxShadow: "none" } },
              '& [class*="AppCombobox--input"]': {
                flexBasis: "min-content",
                padding: spacing.none,
                margin: `${spacing.xs} ${spacing.none}`,
                flexGrow: "1",
                transform: `translateY(${spacing.xxs})`,
                "&:focus": { minWidth: "15ch" },
              },
            },
          },
        },
        '&[class*="AppCombobox--layout--fixed"]': {
          '& [class*="AppCombobox--textField"]': {
            '& [class*="MuiInput-root"]': {
              flexWrap: "nowrap",
              paddingTop: spacing.xs,
              '& [class*="MuiChip-root"]': { marginTop: spacing.none },
              '& [class*="AppCombobox--input"]': {
                padding: spacing.none,
                marginLeft: spacing.xxs,
              },
            },
          },
        },
        '& [class*="AppSearch--textField"]': {
          backgroundColor: color.searchFieldFill,
          borderRadius: spacing.xxs,
          height: "40px",
          '& [class*="MuiAutocomplete-inputRoot"]': {
            borderRadius: spacing.xxs,
            border: `${borderWidth.thin} solid ${color.transparent}`,
            boxShadow: "none",
            flexWrap: "nowrap",
            height: "100%",
            ...transition.blur,
            "&.Mui-focused": {
              border: `1px solid ${color.iris}`,
              ...transition.activate,
            },
            "&.Mui-disabled.Mui-disabled": {
              '& [class*="MuiInputAdornment-root"]': {
                opacity: opacity.disabled,
              },
              // Weave dims the whole input root as well, so the field was
              // dimmed twice; DHIG dims only the adornment.
              opacity: 1,
              pointerEvents: "none",
              color: color.ink,
            },
            "&:focus, &:focus:active": {
              border: `${borderWidth.thick} solid ${color.iris}`,
              ...transition.activate,
            },
            "&:active": {
              border: `${borderWidth.thin} solid ${color.iris}`,
              ...transition.activate,
            },
            "&:hover, &.Mui-focused:not(:hover)": {
              backgroundColor: color.hoverFillSubtle,
              ...transition.activate,
              '&[class*="MuiInput-root"]': { boxShadow: "none" },
            },
            '& [class*="MuiInputAdornment-root"]': {
              width: "unset",
              paddingLeft: spacing.xs,
              paddingRight: spacing.none,
              marginRight: spacing.none,
              '& [class*="MuiSvgIcon-root"]': {
                height: "24px",
                width: "unset",
              },
            },
          },
          '& [class*="AppSearch--input"]': { padding: spacing.xxs },
          '& [class*="MuiAutocomplete-endAdornment"]': {
            top: "unset",
            // Weave offsets the end adornment 8px from the edge; DHIG's
            // Search does not.
            right: spacing.none,
            marginRight: spacing.xs,
          },
        },
        // Weave pins every end adornment button to black. Unset it so the
        // Search clear button keeps the MUI icon colour, as under DHIG
        // (Combobox sets its own colour above).
        "& .MuiTextField-root": {
          "& .MuiAutocomplete-endAdornment": {
            "& button": { color: undefined },
          },
        },
        '& [class*="MuiTextField-root"]': {
          minHeight: "40px",
          '& [class*="MuiInput-root"]': {
            paddingBottom: spacing.none,
            color: color.ink,
            '& [class*="MuiAutocomplete-endAdornment"]': {
              height: spacing.l,
              width: spacing.l,
              ...transition.blur,
              '& [class*="MuiAutocomplete-clearIndicator"]': {
                visibility: "unset",
                opacity: 0,
                '& [class*="MuiSvgIcon-root"]': {
                  color: color.ink,
                },
                "&:hover": {
                  backgroundColor: color.hoverFillSubtle,
                  ...transition.activate,
                },
                "&:active": {
                  backgroundColor: color.pressedFillSubtle,
                  ...transition.activate,
                },
                "&:focus, &:focus-visible": {
                  outline: "none",
                  boxShadow: effect.focusRingThin,
                  ...transition.activate,
                  "&:hover, &:active": {
                    backgroundColor: color.surface,
                  },
                },
              },
            },
          },
          "&:hover, &:focus-within": {
            '& [class*="MuiAutocomplete-endAdornment"]': {
              '& [class*="MuiAutocomplete-clearIndicator"]': { opacity: 1 },
            },
          },
          "& [class*='MuiInput-root']": {
            "& [class*='MuiAutocomplete-endAdornment']": {
              minHeight: "40px",
              top: "0",
              transform: "unset",
              maxHeight: spacing.xxl,
              height: "100%",
              display: "flex",
              alignItems: "center",
              "& [class*='MuiButtonBase-root']": { height: spacing.l },
            },
          },
        },
      },
    },
  },
};

/**
 * DHIG's rules for the Autocomplete popup (its `MuiPopper`
 * theme slot): the listbox paper's shadow, square top corners and
 * 4px margin, and the option rows' padding, selected, hover, pressed and focus
 * states. Weave's `MuiPopper` slot is an object, so this plain object
 * deep-merges with it. The tooltip (`DhigFlyout`) half of DHIG's slot is not
 * carried over.
 */
export const autocompletePopperOverrides: ThemeComponents = {
  MuiPopper: {
    styleOverrides: {
      root: {
        '&[class*="MuiAutocomplete-popper"]': {
          '& [class*="MuiPaper-root"]': {
            ...typographyVariant["body-copy-medium"],
            color: color.ink,
            boxShadow: effect.dropdown,
            borderRadius: `${radius.none} ${radius.none} ${radius.medium} ${radius.medium}`,
            margin: `${spacing.xxs} ${spacing.none}`,
            backgroundColor: color.surface,
            '& [class*="MuiAutocomplete-noOptions"]': {
              color: color.ink,
            },
            '& [class*="MuiAutocomplete-listbox"]': {
              '& [class*="MuiAutocomplete-option"]': {
                cursor: "pointer",
                display: "flex",
                boxSizing: "border-box",
                alignItems: "center",
                padding: `10px ${spacing.s}`,
                justifyContent: "flex-start",
                ...transition.blur,
                '&[aria-selected="true"]': {
                  backgroundColor: color.activatedFillSubtle,
                },
                '&[aria-disabled="true"]': {
                  opacity: "0.3",
                  pointerEvents: "none",
                },
                "&.Mui-focused:hover": {
                  backgroundColor: color.hoverFillSubtle,
                  ...transition.activate,
                },
                "&.Mui-focused:active, &:active": {
                  backgroundColor: color.pressedFillSubtle,
                  ...transition.activate,
                },
                "&.Mui-focused.Mui-focusVisible": {
                  boxShadow: `inset ${effect.focusRingThin}`,
                  ...transition.activate,
                },
                '& [class*="AppCombobox--checkedIcon"]': {
                  position: "absolute",
                  right: spacing.s,
                },
                '& [class*="MuiCheckbox-root"]': {
                  marginRight: spacing.xs,
                },
                '&[class*="AppCombobox--selectAllOption"]': {
                  borderBottom: `${borderWidth.thin} solid ${color.divider}`,
                },
              },
              "& li": {
                '[class*="MuiListSubheader-root"]': {
                  borderTop: `${borderWidth.thin} solid ${color.rowDivider}`,
                  marginLeft: "1px",
                  marginRight: "1px",
                  background: gradient.categoryTitle,
                },
                '&:first-of-type [class*="MuiListSubheader-root"]': {
                  borderTop: "none",
                },
              },
            },
            '& [class*="MuiListSubheader-root"]': {
              backgroundColor: color.transparent,
            },
            '& [class*="AppCombobox--addendum"]': {
              color: color.inkMuted,
              ...typographyVariant["body-copy-small"],
              padding: spacing.s,
            },
          },
        },
      },
    },
  },
};
